import { useState, useRef, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  CheckIcon,
  PlayIcon,
  PauseIcon,
} from '@heroicons/react/24/outline';
import { Card, CardBody, CardHeader } from '@/components/common/Card';
import Button from '@/components/common/Button';
import Badge from '@/components/common/Badge';
import { PageSpinner } from '@/components/common/Spinner';
import toast from 'react-hot-toast';
import {
  getEmailTemplate,
  updateEmailTemplate,
  getTemplateVersions,
  revertToVersion,
  toggleTemplateActive,
  sendTestEmail,
} from '@/services/emailTemplateApi';
import TipTapEditor, { TipTapEditorRef } from '@/components/modules/email-templates/TipTapEditor';
import VariableSidebar from '@/components/modules/email-templates/VariableSidebar';
import TemplatePreview from '@/components/modules/email-templates/TemplatePreview';
import VersionTimeline from '@/components/modules/email-templates/VersionTimeline';
import { TRIGGER_TYPE_LABELS, CADENCE_LABELS } from '@/types/emailTemplate';

type TabType = 'content' | 'settings' | 'trigger' | 'variables' | 'history';

export default function EmailTemplateDetail() {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const editorRef = useRef<TipTapEditorRef>(null);

  // Local state
  const [activeTab, setActiveTab] = useState<TabType>('content');
  const [isHtmlMode, setIsHtmlMode] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Form state
  const [subject, setSubject] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [fromName, setFromName] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [replyToEmail, setReplyToEmail] = useState('');
  const [ccAddresses, setCcAddresses] = useState('');
  const [bccAddresses, setBccAddresses] = useState('');
  const [changeSummary, setChangeSummary] = useState('');

  // Fetch template
  const { data: template, isLoading } = useQuery({
    queryKey: ['emailTemplate', templateId],
    queryFn: () => getEmailTemplate(templateId!),
    enabled: !!templateId,
  });

  // Initialize form state when template data loads
  useEffect(() => {
    if (template) {
      setSubject(template.subject_template);
      setHtmlContent(template.html_content);
      setFromName(template.from_name);
      setFromEmail(template.from_email);
      setReplyToEmail(template.reply_to_email || '');
      setCcAddresses(template.cc_addresses?.join(', ') || '');
      setBccAddresses(template.bcc_addresses?.join(', ') || '');
      setIsDirty(false);
    }
  }, [template]);

  // Fetch versions
  const { data: versionsData } = useQuery({
    queryKey: ['emailTemplateVersions', templateId],
    queryFn: () => getTemplateVersions(templateId!),
    enabled: !!templateId && activeTab === 'history',
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!templateId) return;
      return updateEmailTemplate(templateId, {
        subject_template: subject,
        html_content: htmlContent,
        from_name: fromName,
        from_email: fromEmail,
        reply_to_email: replyToEmail || null,
        cc_addresses: ccAddresses ? ccAddresses.split(',').map(e => e.trim()).filter(Boolean) : null,
        bcc_addresses: bccAddresses ? bccAddresses.split(',').map(e => e.trim()).filter(Boolean) : null,
        change_summary: changeSummary || 'Template updated',
      });
    },
    onSuccess: () => {
      toast.success('Template saved successfully');
      setIsDirty(false);
      setChangeSummary('');
      queryClient.invalidateQueries({ queryKey: ['emailTemplate', templateId] });
      queryClient.invalidateQueries({ queryKey: ['emailTemplateVersions', templateId] });
      queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });
    },
    onError: () => toast.error('Failed to save template'),
  });

  // Toggle active mutation
  const toggleActiveMutation = useMutation({
    mutationFn: async () => {
      if (!templateId || !template) return;
      return toggleTemplateActive(templateId, !template.is_active);
    },
    onSuccess: () => {
      toast.success(template?.is_active ? 'Template deactivated' : 'Template activated');
      queryClient.invalidateQueries({ queryKey: ['emailTemplate', templateId] });
      queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });
    },
    onError: () => toast.error('Failed to update template status'),
  });

  // Revert mutation
  const revertMutation = useMutation({
    mutationFn: async (versionId: string) => {
      if (!templateId) return;
      return revertToVersion(templateId, versionId);
    },
    onSuccess: (data) => {
      toast.success('Template reverted successfully');
      if (data) {
        setSubject(data.subject_template);
        setHtmlContent(data.html_content);
      }
      queryClient.invalidateQueries({ queryKey: ['emailTemplate', templateId] });
      queryClient.invalidateQueries({ queryKey: ['emailTemplateVersions', templateId] });
    },
    onError: () => toast.error('Failed to revert template'),
  });

  // Send test email mutation
  const sendTestMutation = useMutation({
    mutationFn: async (testEmail: string) => {
      if (!templateId) return;
      return sendTestEmail({
        template_id: templateId,
        test_email: testEmail,
      });
    },
    onSuccess: (data) => {
      if (data?.success) {
        toast.success('Test email sent successfully');
      } else {
        toast.error(data?.message || 'Failed to send test email');
      }
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to send test email'),
  });

  // Handle variable insertion
  const handleInsertVariable = useCallback((variableKey: string) => {
    if (editorRef.current) {
      editorRef.current.insertVariable(variableKey);
    }
  }, []);

  // Mark as dirty when content changes
  const handleContentChange = (newContent: string) => {
    setHtmlContent(newContent);
    setIsDirty(true);
  };

  const handleSubjectChange = (newSubject: string) => {
    setSubject(newSubject);
    setIsDirty(true);
  };

  if (isLoading) {
    return <PageSpinner />;
  }

  if (!template) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Template not found</p>
        <Button
          variant="secondary"
          className="mt-4"
          onClick={() => navigate('/email-templates')}
        >
          Back to Templates
        </Button>
      </div>
    );
  }

  const tabs: { id: TabType; label: string }[] = [
    { id: 'content', label: 'Content' },
    { id: 'settings', label: 'Settings' },
    { id: 'trigger', label: 'Trigger Info' },
    { id: 'variables', label: 'Variables' },
    { id: 'history', label: 'History' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/email-templates')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">{template.name}</h1>
              <Badge variant={template.is_active ? 'success' : 'gray'} dot>
                {template.is_active ? 'Active' : 'Inactive'}
              </Badge>
              {template.is_system && <Badge variant="info">System</Badge>}
            </div>
            <p className="mt-1 text-sm text-gray-500 font-mono">{template.slug}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {isDirty && (
            <span className="text-sm text-orange-600">Unsaved changes</span>
          )}
          <Button
            variant="secondary"
            onClick={() => toggleActiveMutation.mutate()}
            loading={toggleActiveMutation.isPending}
            leftIcon={template.is_active ? <PauseIcon className="w-4 h-4" /> : <PlayIcon className="w-4 h-4" />}
          >
            {template.is_active ? 'Deactivate' : 'Activate'}
          </Button>
          <Button
            onClick={() => updateMutation.mutate()}
            loading={updateMutation.isPending}
            disabled={!isDirty}
            leftIcon={<CheckIcon className="w-4 h-4" />}
          >
            Save Changes
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-brand text-brand'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content Area */}
        <div className="lg:col-span-2 space-y-6">
          {activeTab === 'content' && (
            <>
              {/* Subject Line */}
              <Card>
                <CardHeader>
                  <h3 className="font-medium">Subject Line</h3>
                </CardHeader>
                <CardBody>
                  <input
                    id="template-subject"
                    name="subject"
                    type="text"
                    value={subject}
                    onChange={(e) => handleSubjectChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                    placeholder="Email subject..."
                    aria-label="Email subject"
                  />
                  <p className="mt-2 text-xs text-gray-500">
                    Use {'{{variable}}'} syntax to insert dynamic content
                  </p>
                </CardBody>
              </Card>

              {/* HTML Editor */}
              <Card>
                <CardHeader>
                  <h3 className="font-medium">Email Body</h3>
                </CardHeader>
                <CardBody padding="none">
                  <TipTapEditor
                    ref={editorRef}
                    content={htmlContent}
                    onChange={handleContentChange}
                    isHtmlMode={isHtmlMode}
                    onHtmlModeChange={setIsHtmlMode}
                  />
                </CardBody>
              </Card>

              {/* Change Summary (when saving) */}
              {isDirty && (
                <Card>
                  <CardBody>
                    <label htmlFor="template-change-summary" className="block text-sm font-medium text-gray-700 mb-1">
                      Change Summary (optional)
                    </label>
                    <input
                      id="template-change-summary"
                      name="changeSummary"
                      type="text"
                      value={changeSummary}
                      onChange={(e) => setChangeSummary(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                      placeholder="Briefly describe your changes..."
                      aria-label="Change summary"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      This will be saved in the version history
                    </p>
                  </CardBody>
                </Card>
              )}
            </>
          )}

          {activeTab === 'settings' && (
            <Card>
              <CardHeader>
                <h3 className="font-medium">Email Settings</h3>
              </CardHeader>
              <CardBody className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="template-from-name" className="block text-sm font-medium text-gray-700 mb-1">
                      From Name
                    </label>
                    <input
                      id="template-from-name"
                      name="fromName"
                      type="text"
                      value={fromName}
                      onChange={(e) => { setFromName(e.target.value); setIsDirty(true); }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                      aria-label="From name"
                    />
                  </div>
                  <div>
                    <label htmlFor="template-from-email" className="block text-sm font-medium text-gray-700 mb-1">
                      From Email
                    </label>
                    <input
                      id="template-from-email"
                      name="fromEmail"
                      type="email"
                      value={fromEmail}
                      onChange={(e) => { setFromEmail(e.target.value); setIsDirty(true); }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                      aria-label="From email"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="template-reply-to-email" className="block text-sm font-medium text-gray-700 mb-1">
                    Reply-To Email (optional)
                  </label>
                  <input
                    id="template-reply-to-email"
                    name="replyToEmail"
                    type="email"
                    value={replyToEmail}
                    onChange={(e) => { setReplyToEmail(e.target.value); setIsDirty(true); }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                    placeholder="Leave empty to use From email"
                    aria-label="Reply-to email"
                  />
                </div>

                <div>
                  <label htmlFor="template-cc-addresses" className="block text-sm font-medium text-gray-700 mb-1">
                    CC Addresses (comma-separated)
                  </label>
                  <input
                    id="template-cc-addresses"
                    name="ccAddresses"
                    type="text"
                    value={ccAddresses}
                    onChange={(e) => { setCcAddresses(e.target.value); setIsDirty(true); }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                    placeholder="email1@example.com, email2@example.com"
                    aria-label="CC addresses"
                  />
                </div>

                <div>
                  <label htmlFor="template-bcc-addresses" className="block text-sm font-medium text-gray-700 mb-1">
                    BCC Addresses (comma-separated)
                  </label>
                  <input
                    id="template-bcc-addresses"
                    name="bccAddresses"
                    type="text"
                    value={bccAddresses}
                    onChange={(e) => { setBccAddresses(e.target.value); setIsDirty(true); }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                    placeholder="email1@example.com, email2@example.com"
                    aria-label="BCC addresses"
                  />
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Default Recipient</h4>
                  <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
                    {template.default_to || 'Determined by edge function at send time'}
                  </p>
                </div>
              </CardBody>
            </Card>
          )}

          {activeTab === 'trigger' && (
            <Card>
              <CardHeader>
                <h3 className="font-medium">Trigger Information</h3>
                <p className="text-sm text-gray-500">
                  This information describes how and when this email is sent
                </p>
              </CardHeader>
              <CardBody className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="block text-sm font-medium text-gray-700 mb-1">
                      Trigger Type
                    </p>
                    <p className="text-sm text-gray-900">
                      {TRIGGER_TYPE_LABELS[template.trigger_type]}
                    </p>
                  </div>
                  <div>
                    <p className="block text-sm font-medium text-gray-700 mb-1">
                      Cadence
                    </p>
                    <p className="text-sm text-gray-900">
                      {template.cadence ? CADENCE_LABELS[template.cadence] || template.cadence : 'N/A'}
                    </p>
                  </div>
                </div>

                <div>
                  <p className="block text-sm font-medium text-gray-700 mb-1">
                    Trigger Description
                  </p>
                  <p className="text-sm text-gray-900 bg-gray-50 rounded-lg p-3">
                    {template.trigger_description}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="block text-sm font-medium text-gray-700 mb-1">
                      Edge Function
                    </p>
                    <code className="text-sm bg-gray-100 px-2 py-1 rounded font-mono">
                      {template.trigger_function}
                    </code>
                  </div>
                  <div>
                    <p className="block text-sm font-medium text-gray-700 mb-1">
                      Trigger Screen
                    </p>
                    <p className="text-sm text-gray-900">
                      {template.trigger_screen || 'N/A'}
                    </p>
                  </div>
                </div>

                {template.is_system && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-800">
                      This is a system template. Trigger information cannot be modified.
                    </p>
                  </div>
                )}
              </CardBody>
            </Card>
          )}

          {activeTab === 'variables' && (
            <Card>
              <CardHeader>
                <h3 className="font-medium">Template Variables</h3>
                <p className="text-sm text-gray-500">
                  Variables that can be used in this template
                </p>
              </CardHeader>
              <CardBody>
                {template.variables && template.variables.length > 0 ? (
                  <div className="space-y-3">
                    {template.variables.map((variable) => (
                      <div
                        key={variable.id}
                        className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <code className="text-sm font-mono text-brand bg-brand/10 px-2 py-0.5 rounded">
                                {`{{${variable.variable_key}}}`}
                              </code>
                              {variable.is_required && (
                                <Badge variant="danger">Required</Badge>
                              )}
                            </div>
                            <p className="mt-1 font-medium text-gray-900">
                              {variable.variable_name}
                            </p>
                            {variable.description && (
                              <p className="mt-1 text-sm text-gray-500">
                                {variable.description}
                              </p>
                            )}
                          </div>
                          <div className="text-right text-sm text-gray-500">
                            <p>Type: {variable.data_type}</p>
                            {variable.example_value && (
                              <p>Example: {variable.example_value}</p>
                            )}
                          </div>
                        </div>
                        {variable.source_table && (
                          <p className="mt-2 text-xs text-gray-400">
                            Source: {variable.source_table}.{variable.source_field}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">
                    No variables defined for this template
                  </p>
                )}
              </CardBody>
            </Card>
          )}

          {activeTab === 'history' && (
            <Card>
              <CardHeader>
                <h3 className="font-medium">Version History</h3>
                <p className="text-sm text-gray-500">
                  All versions of this template are preserved
                </p>
              </CardHeader>
              <CardBody>
                <VersionTimeline
                  versions={versionsData?.versions || []}
                  currentVersionId={template.current_version?.id}
                  onRevert={(versionId) => revertMutation.mutateAsync(versionId)}
                  isReverting={revertMutation.isPending}
                />
              </CardBody>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Variable Sidebar - always visible on content tab */}
          {activeTab === 'content' && template.variables && (
            <VariableSidebar
              variables={template.variables}
              onInsertVariable={handleInsertVariable}
            />
          )}

          {/* Preview - visible on content and settings tabs */}
          {(activeTab === 'content' || activeTab === 'settings') && (
            <TemplatePreview
              subject={subject}
              htmlContent={htmlContent}
              variables={template.variables || []}
              fromName={fromName}
              fromEmail={fromEmail}
              onSendTest={(email) => sendTestMutation.mutateAsync(email)}
              isSendingTest={sendTestMutation.isPending}
            />
          )}

          {/* Template Info Card */}
          <Card>
            <CardHeader>
              <h3 className="font-medium">Template Info</h3>
            </CardHeader>
            <CardBody className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Slug</span>
                <code className="font-mono text-gray-900">{template.slug}</code>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Status</span>
                <Badge variant={template.is_active ? 'success' : 'gray'}>
                  {template.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Current Version</span>
                <span className="font-mono text-gray-900">
                  {template.current_version?.version_label || 'v1'}
                </span>
              </div>
              {template.description && (
                <div className="pt-3 border-t border-gray-200">
                  <p className="text-gray-500 mb-1">Description</p>
                  <p className="text-gray-900">{template.description}</p>
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}
